import { useTranslate } from '@ur-solutions/react-hooks'
import type { FilterChildren } from '@/components_deprecated/FilterPopup'
import { FilterPopup } from '@/components_deprecated/FilterPopup'
import {
  FilterOptionsStack,
  StyledFilterCheckbox,
} from '@/components_deprecated/FilterPopup/filter-option-styles'
import {
  FilterCheckmarksWrapper,
  Status,
  StatusButton,
} from '@/components_deprecated/FilterPopup/components'
import React from 'react'
import type { InfringementCategoryIdentifiers } from '../consts'
import { InfringementSeverities, InfringementStatuses } from '../consts'
import type {
  InfringementsTableFilter,
  InfringementStatus,
  SeverityCategory,
} from '../types'
import {
  useInfringementSeverityFunctions,
  useInfringementCategoryFunctions,
} from '../util'

export const InfringementPopupFilter: React.FC<
  FilterChildren<InfringementsTableFilter>
> = ({ filter, onUpdate }) => {
  const translations = useTranslate({
    archived: 'common.archived',
    status: 'common.status',
    all: 'common.all',
    open: 'common.open-alt',
    closed: 'common.closed',

    severities: 'infringements.severities',
    categories: 'common.categories',

    minorInfringement: 'infringements.minor-infringement',
    seriousInfringement: 'infringements.serious-infringement',
    verySeriousInfringement: 'infringements.very-serious-infringement',
    mostSeriousInfringement: 'infringements.most-serious-infringement',
    infringementPoints: ['infringements.n-points', { n: 0 }],
  })
  const severities = Object.keys(InfringementSeverities) as SeverityCategory[]
  const categoryIdentifiers: InfringementCategoryIdentifiers[] = ['B', 'C', 'D']

  const allSeveritiesSelected = filter.excludeSeverities.length === 0
  const selectedSeveritiesCount =
    severities.length - filter.excludeSeverities.length
  const excludedCategoryIdentifiers = new Set(
    filter.excludeCategories.map(category => category[0])
  )
  const allCategoriesSelected = excludedCategoryIdentifiers.size === 0
  const selectedCategoriesCount =
    categoryIdentifiers.length - excludedCategoryIdentifiers.size

  function toggleStatus(status: InfringementStatus) {
    return () => {
      const isActive = filter.statuses.includes(status)
      if (isActive) {
        if (filter.statuses.length === 1) return
        onUpdate({ statuses: filter.statuses.filter(s => s !== status) })
        return
      }
      onUpdate({ statuses: [...filter.statuses, status] })
    }
  }

  function toggleSeverity(severity: SeverityCategory) {
    return () => {
      if (allSeveritiesSelected) {
        onUpdate({
          excludeSeverities: severities.filter(current => current !== severity),
        })
        return
      }

      const isExcluded = filter.excludeSeverities.includes(severity)

      if (isExcluded) {
        onUpdate({
          excludeSeverities: filter.excludeSeverities.filter(
            s => s !== severity
          ),
        })
        return
      }

      if (selectedSeveritiesCount <= 1) {
        onUpdate({
          excludeSeverities: [],
        })
        return
      }

      onUpdate({
        excludeSeverities: [...filter.excludeSeverities, severity],
      })
    }
  }

  function toggleCategories(category: InfringementCategoryIdentifiers) {
    return () => {
      const allCategoryExcludingCurrent = categoryIdentifiers
        .filter(identifier => identifier !== category)
        .flatMap(getCategoriesFromIdentifier)

      if (allCategoriesSelected) {
        onUpdate({
          excludeCategories: allCategoryExcludingCurrent,
        })
        return
      }

      const excludedIdentifiers = new Set(
        filter.excludeCategories.map(currentCategory => currentCategory[0])
      )
      const isExcluded = excludedIdentifiers.has(category)

      if (isExcluded) {
        onUpdate({
          excludeCategories: filter.excludeCategories.filter(
            currentCategory => currentCategory[0] !== category
          ),
        })
        return
      }

      if (selectedCategoriesCount <= 1) {
        onUpdate({
          excludeCategories: [],
        })
        return
      }

      onUpdate({
        excludeCategories: [
          ...filter.excludeCategories,
          ...getCategoriesFromIdentifier(category),
        ],
      })
    }
  }

  const { getSeverityTranslation, getSeverityValue } =
    useInfringementSeverityFunctions()

  const { getCategoryTranslation, getCategoriesFromIdentifier } =
    useInfringementCategoryFunctions()

  return (
    <FilterPopup>
      <h4>{translations.status}</h4>
      <Status statusCount={3}>
        <StatusButton
          active={filter.statuses.includes(InfringementStatuses.OPEN)}
          onClick={toggleStatus(InfringementStatuses.OPEN)}
        >
          {translations.open}
        </StatusButton>
        <StatusButton
          active={filter.statuses.includes(InfringementStatuses.CLOSED)}
          onClick={toggleStatus(InfringementStatuses.CLOSED)}
        >
          {translations.closed}
        </StatusButton>
        <StatusButton
          active={filter.statuses.includes(InfringementStatuses.ARCHIVED)}
          onClick={toggleStatus(InfringementStatuses.ARCHIVED)}
        >
          {translations.archived}
        </StatusButton>
      </Status>

      <h4>{translations.severities}</h4>

      <FilterCheckmarksWrapper>
        <FilterOptionsStack>
          <StyledFilterCheckbox
            checked={allSeveritiesSelected}
            label={translations.all}
            boxWidth="24px"
            boxHeight="24px"
            fontSize="0.8rem"
            onChange={() =>
              onUpdate({
                excludeSeverities: [],
              })
            }
          />
          {severities.map(severity => {
            const checked =
              !allSeveritiesSelected &&
              !filter.excludeSeverities.includes(severity)
            const label = `${getSeverityTranslation(
              severity
            )} (${translations.infringementPoints({
              n: getSeverityValue(severity),
            })})`
            return (
              <StyledFilterCheckbox
                key={severity}
                checked={checked}
                label={label}
                boxWidth="24px"
                boxHeight="24px"
                fontSize="0.8rem"
                onChange={toggleSeverity(severity)}
              />
            )
          })}
        </FilterOptionsStack>
      </FilterCheckmarksWrapper>

      <h4>{translations.categories}</h4>

      <FilterCheckmarksWrapper>
        <FilterOptionsStack>
          <StyledFilterCheckbox
            checked={allCategoriesSelected}
            label={translations.all}
            boxWidth="24px"
            boxHeight="24px"
            fontSize="0.8rem"
            onChange={() =>
              onUpdate({
                excludeCategories: [],
              })
            }
          />
          {categoryIdentifiers.map(categoryIdentifier => {
            const checked =
              !allCategoriesSelected &&
              !excludedCategoryIdentifiers.has(categoryIdentifier)
            const label = getCategoryTranslation(categoryIdentifier)

            return (
              <StyledFilterCheckbox
                key={categoryIdentifier}
                checked={checked}
                label={label}
                boxWidth="24px"
                boxHeight="24px"
                fontSize="0.8rem"
                onChange={toggleCategories(categoryIdentifier)}
              />
            )
          })}
        </FilterOptionsStack>
      </FilterCheckmarksWrapper>
    </FilterPopup>
  )
}
